import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'
import { Pressable } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import {
  LYRA_DISPLAY_LANGUAGES,
  RIGHT_TO_LEFT_LANGUAGES,
} from '@lyrahealth-inc/shared-app-logic/src/features/intl/constants'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { CheckIcon } from '../../atoms/icons/CheckIcon'
import { QuestionMarkIcon } from '../../atoms/icons/QuestionMarkIcon'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

interface LanguageSelectorModalProps {
  activeLanguage: string
  availableLanguages: readonly string[]
  beforeLaunch?: boolean
  onClick: (selectedLocale: string) => void
  onGetHelpPress?: () => void
}

const ModalBody = styled.View<{
  theme: ThemeType
}>(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  padding: theme.spacing['24px'],
}))

const LanguageList = styled.View<{
  languageCount: number
  theme: ThemeType
}>(({ theme, languageCount }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: theme.breakpoints.isMobileSized ? 'nowrap' : 'wrap',
  rowGap: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['12px'],
  columnGap: theme.spacing['40px'],
  marginBottom: 0,
  paddingBottom: 0,
  ...(!theme.breakpoints.isMobileSized && {
    flexWrap: 'wrap',
    height: Math.ceil(languageCount / 3) * 37 - 12,
    width: '600px',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  }),
}))

const LanguageButton = styled(Pressable)<{
  isActiveLanguage: boolean
  theme: ThemeType
}>(({ theme, isActiveLanguage }) => ({
  marginLeft: 0,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'left',
  padding: 0,
  fontWeight: '400',
  backgroundColor: 'transparent',
  borderWidth: 0,
  height: '25px',
  minHeight: theme.spacing['16px'],
  ...(isActiveLanguage && {
    color: theme.colors.linkDefault,
  }),
}))

const LanguageButtonText = styled(BodyText)<{
  isActiveLanguage: boolean
  theme: ThemeType
}>(({ theme, isActiveLanguage }) => ({
  color: isActiveLanguage ? theme.colors.linkDefault : theme.colors.textPrimary,
  fontWeight: '400',
}))

const CheckIconContainer = styled.View(({ theme }: { theme: ThemeType }) => ({
  marginLeft: theme.spacing['8px'],
}))

const CareTeamButton = styled(SecondaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  margin: `${theme.spacing['16px']} 0 0 0`,
  width: '100%',
  minHeight: theme.spacing['48px'],
}))

export const LanguageSelectorModal: FunctionComponent<LanguageSelectorModalProps> = ({
  activeLanguage,
  availableLanguages,
  beforeLaunch = false,
  onClick,
  onGetHelpPress,
}) => {
  const { colors } = useTheme()

  const languageButtons = availableLanguages.map((language) => {
    const isActiveLanguage = activeLanguage === language
    const languageName = LYRA_DISPLAY_LANGUAGES[language as keyof typeof LYRA_DISPLAY_LANGUAGES]?.name || language

    return (
      <LanguageButton
        key={language}
        isActiveLanguage={isActiveLanguage}
        onPress={() => onClick(language)}
        testID={tID(`DisplayLanguage-${language}-button`)}
        accessibilityRole={Object.prototype.hasOwnProperty.call(RIGHT_TO_LEFT_LANGUAGES, language) ? 'link' : 'button'}
        accessibilityLabel={`${languageName}${isActiveLanguage ? ' (selected)' : ''}`}
      >
        <LanguageButtonText lang={language} isActiveLanguage={isActiveLanguage} size={BodyTextSize.DEFAULT}>
          {languageName}
        </LanguageButtonText>
        {isActiveLanguage && (
          <CheckIconContainer>
            <CheckIcon fillColor={colors.linkDefault} isFilled size={24} withCircle={false} />
          </CheckIconContainer>
        )}
      </LanguageButton>
    )
  })

  return (
    <ModalBody testID={tID('LanguageSelectorModal')}>
      <LanguageList languageCount={availableLanguages.length}>{languageButtons}</LanguageList>
      {!beforeLaunch && onGetHelpPress && (
        <CareTeamButton
          testID={tID('GetHelpButton')}
          onPress={onGetHelpPress}
          fullWidth
          leftIcon={<QuestionMarkIcon width={24} isFilled />}
          text={
            <FormattedMessage
              defaultMessage='Get help'
              description='Button that links to a contact page, which contains a list of international phone numbers to contact the care navigator team in a specific language'
            />
          }
        />
      )}
    </ModalBody>
  )
}
